import { createMMKV } from "react-native-mmkv";

import type { ContentDto, ProfileDto } from "$src/core/models/dto";

export const storage = createMMKV({ id: "storage" });

export type StorageItems = {
  "auth.profileId": string;
  profiles: ProfileDto[];
  "search.conversational.status": "enabled" | "disabled";
  "search.recent.status": "enabled" | "disabled";
  "search.recent.items": ContentDto[];
  "tv-connect.known-client-ids": string[];
  watchlist: Record<string, string[]>;
};

// Util type that ensures the string type of the key corresponds with the actual type of it
type StorageItemTypesByString = {
  [K in keyof StorageItems]: StorageItems[K] extends string
    ? "string"
    : StorageItems[K] extends number
      ? "number"
      : StorageItems[K] extends boolean
        ? "boolean"
        : StorageItems[K] extends object
          ? "object"
          : never;
};

const StorageItemTypes: StorageItemTypesByString = {
  "auth.profileId": "string",
  profiles: "object",
  "search.conversational.status": "string",
  "search.recent.items": "object",
  "search.recent.status": "string",
  "tv-connect.known-client-ids": "object",
  watchlist: "object",
};

const Storage = {
  get: <K extends keyof StorageItems>(key: K): StorageItems[K] | null => {
    const type = StorageItemTypes[key];

    if (type === "string") {
      return (storage.getString(key) ?? null) as StorageItems[K] | null;
    }
    if (type === "number") {
      return (storage.getNumber(key) ?? null) as StorageItems[K] | null;
    }
    if (type === "boolean") {
      return (storage.getBoolean(key) ?? null) as StorageItems[K] | null;
    }
    if (type === "object") {
      const value = storage.getString(key) ?? null;
      if (value === null) return null;

      return JSON.parse(value) as StorageItems[K];
    }

    throw new Error("Unsupported value type");
  },

  set: <K extends keyof StorageItems>(key: K, value: StorageItems[K]) => {
    const type = StorageItemTypes[key];

    if (type === "string") {
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      return storage.set(key, value.toString());
    }
    if (type === "number") {
      return storage.set(key, Number(value));
    }
    if (type === "boolean") {
      return storage.set(key, Boolean(value));
    }
    if (type === "object") {
      return storage.set(key, JSON.stringify(value));
    }

    throw new Error("Unsupported value type");
  },

  remove: <K extends keyof StorageItems>(key: K) => storage.remove(key),

  subscribe: <K extends keyof StorageItems>(
    key: K,
    cb: (value: StorageItems[K] | null) => void,
  ) => {
    const listener = storage.addOnValueChangedListener((changedKey) => {
      if (key === changedKey) {
        cb(Storage.get(key));
      }
    });
    return listener.remove;
  },

  clear: () => storage.clearAll(),
};

export default Storage;
